import { BlockingPopupModal } from "@/components";
import PageWrapper from "@/components/PageWrapper/PageWrapper";
import { SaveAndCancel } from "@/components/StickyActionsHeader/SaveActions/SaveAndCancel";
import { StickyActionsHeader } from "@/components/StickyActionsHeader/StickyActionsHeader";
import { FieldNames, FormType } from "@/enums";
import { IFile } from "@/interfaces/IFile";
import { ISelectOption } from "@/interfaces";
import { useFormPropertyValueLookupProvider } from "@/providers";
import {
  useCreateInitiativeMutation,
  useDeleteUploadsMutation,
  useGetCDNFilePathQuery,
  useGetFormLookupsQuery,
  useGetInitiativeDocumentsQuery,
  useGetInitiativeProjectTypesQuery,
  useGetInitiativeQuery,
  useSoftDeleteInitiativeDocumentMutation,
  useUpdateInitiativeMutation,
  useUploadInitiativeDocumentMutation,
} from "@/services";
import { Form, UploadFile, UploadProps } from "antd";
import { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { v4 } from "uuid";
import dayjs from "dayjs";
import { InitiativeFormFields } from "./components/InitiativeFormFields";
import { InitiativePreviewModal } from "./components/InitiativePreviewModal";
import { routes } from "@/helpers/routes";

const PAGE_NAME = "initiative";

interface IInitiativeFormValues {
  name: string;
  projectType: string;
  productCategory: string;
  estimatedTargetLaunchDate: dayjs.Dayjs;
  documents: IFile[];
}

export const Initiative = (): React.JSX.Element => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { initiativePublicId } = useParams();
  const [form] = Form.useForm<IInitiativeFormValues>();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [productCategoryOptions, setProductCategoryOptions] = useState<
    ISelectOption[]
  >([]);
  const [projectTypeOptions, setProjectTypeOptions] = useState<
    ISelectOption[]
  >([]);

  const [createInitiative] = useCreateInitiativeMutation();
  const [updateInitiative] = useUpdateInitiativeMutation();
  const [uploadInitiativeDocument] = useUploadInitiativeDocumentMutation();
  const [softDeleteInitiativeDocument] = useSoftDeleteInitiativeDocumentMutation();
  const [deleteUploads] = useDeleteUploadsMutation();
  const [placeholderId] = useState<string>(() => v4());
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [previewFile, setPreviewFile] = useState<UploadFile[]>([]);
  const [fileId, setFileId] = useState<string | null>(null);
  const { data: cdnFilePath } = useGetCDNFilePathQuery(
    {
      projectId: initiativePublicId,
      fileId,
      isInitiative: true,
    },
    { skip: !fileId }
  );

  const { data: formLookups } = useGetFormLookupsQuery();
  const { data: initiativeProjectTypes } = useGetInitiativeProjectTypesQuery();
  const { getPropertyValueDisplay } = useFormPropertyValueLookupProvider();
  const { data: initiative } = useGetInitiativeQuery(initiativePublicId, {
    skip: !initiativePublicId,
  });
  const { data: initiativeDocuments } = useGetInitiativeDocumentsQuery(
    initiativePublicId,
    {
      skip: !initiativePublicId,
    }
  );
  const isEditing = Boolean(initiativePublicId);
  const isUploadingDocuments = fileList.some(
    (file) => file.status === "uploading"
  );
  const existingDocumentIdsRef = useRef<Set<string>>(new Set());
  const pendingDocumentRemovalsRef = useRef<Map<string, string>>(new Map());
  const navigateAfterSaveRef = useRef<string | null>(null);

  useEffect(() => {
    if (!hasUnsavedChanges && navigateAfterSaveRef.current) {
      const path = navigateAfterSaveRef.current;
      navigateAfterSaveRef.current = null;
      navigate(path);
    }
  }, [hasUnsavedChanges, navigate]);

  useEffect(() => {
    const intake = formLookups?.intake;
    if (!intake) return;

    setProductCategoryOptions(
      Object.keys(intake.productCategory).map((key) => ({
        value: key,
        label: getPropertyValueDisplay(
          FormType.Intake,
          FieldNames.ProductCategory,
          key
        ),
      }))
    );
  }, [formLookups?.intake, getPropertyValueDisplay]);

  useEffect(() => {
    if (!Array.isArray(initiativeProjectTypes)) return;

    const options: ISelectOption[] = initiativeProjectTypes.map(
      ({ publicId, name }) => {
        const translatedLabel = getPropertyValueDisplay(
          FormType.Intake,
          FieldNames.ProjectType,
          publicId
        );

        return {
          value: publicId,
          label: translatedLabel === publicId ? name : translatedLabel,
        };
      }
    );

    const savedProjectTypeId = initiative?.publicProjectTypeId;
    if (
      savedProjectTypeId &&
      !options.some((option) => option.value === savedProjectTypeId)
    ) {
      options.push({
        value: savedProjectTypeId,
        label: initiative.projectTypeName ?? savedProjectTypeId,
      });
    }

    setProjectTypeOptions(options);
  }, [initiativeProjectTypes, initiative, getPropertyValueDisplay]);

  useEffect(() => {
    if (!initiative) return;

    form.setFieldsValue({
      name: initiative.name,
      projectType: initiative.publicProjectTypeId,
      productCategory: initiative.publicProductCategoryId,
      estimatedTargetLaunchDate: initiative.estimatedTargetLaunchDate
        ? dayjs(initiative.estimatedTargetLaunchDate)
        : undefined,
    });
  }, [initiative, form]);

  useEffect(() => {
    if (!initiativeDocuments) return;

    const documentFileList: UploadFile[] = (
      initiativeDocuments as Array<{
        publicId: string;
        name: string;
        filePath: string;
        contentType: string;
      }>
    ).map((document) => ({
        uid: String(document.publicId),
        name: document.name,
        status: "done",
        url: document.filePath,
        blobUrl: document.filePath,
        imageUrl:
          document.contentType?.startsWith("image/") && document.filePath,
        type: document.contentType,
      })
    );

    setFileList(documentFileList);
    form.setFieldValue("documents", documentFileList);
    existingDocumentIdsRef.current = new Set(
      documentFileList.map((document) => document.uid)
    );
  }, [initiativeDocuments, form]);

  const setPreviewDoc = (document: UploadFile): void => {
    if (!existingDocumentIdsRef.current.has(document.uid)) {
      toast.error(intl.formatMessage({ id: "initiative.saveBeforePreview" }));
      return;
    }

    setFileId(document.uid);
    setPreviewFile([document]);
  };

  const downloadAndCloseModal = (filePath: string): void => {
    const link = document.createElement("a");
    link.href = filePath;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setIsModalOpen(false);
  };

  useEffect(() => {
    if (fileId && previewFile && cdnFilePath) {
      setIsModalOpen(true);
    }
  }, [fileId, previewFile, cdnFilePath]);

  const handleRemoveDocument = ({ uid, url }: UploadFile): void => {
    if (existingDocumentIdsRef.current.has(uid)) {
      pendingDocumentRemovalsRef.current.set(uid, url ?? "");
      existingDocumentIdsRef.current.delete(uid);
    }

    setFileList((previousFileList) =>
      previousFileList.filter((file) => file.uid !== uid)
    );
    setHasUnsavedChanges(true);
  };

  const handleDocumentsChange: UploadProps["onChange"] = ({
    file: changedFile,
    fileList: updatedFileList,
  }) => {
    setFileList(updatedFileList);
    setHasUnsavedChanges(true);

    if (changedFile.status === "done") {
      form.setFieldValue("documents", updatedFileList);
    }
  };

  const handleCancel = (): void => {
    setHasUnsavedChanges(false);

    if (!initiativePublicId && fileList.length > 0) {
      void deleteUploads({ intakeId: placeholderId, isInitiative: false })
        .unwrap()
        .catch(() => {
          // Best-effort cleanup; don't block navigation if it fails.
        });
    }

    navigate("/initiatives");
  };

  const handleSave = async (): Promise<void> => {
    if (isUploadingDocuments) {
      toast.error(intl.formatMessage({ id: "initiative.documentsUploading" }));
      return;
    }

    let values: IInitiativeFormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    setIsSubmitting(true);
    try {
      const savedinitiativePublicId = isEditing
        ? await updateInitiative({
            initiativePublicId,
            name: values.name,
            publicProductCategoryId: values.productCategory,
            publicProjectTypeId: values.projectType,
            estimatedTargetLaunchDate: values.estimatedTargetLaunchDate.format("YYYY-MM-DD"),
          }).unwrap()
        : await createInitiative({
            name: values.name,
            publicProductCategoryId: values.productCategory,
            publicProjectTypeId: values.projectType,
            estimatedTargetLaunchDate: values.estimatedTargetLaunchDate.format("YYYY-MM-DD"),
          }).unwrap();

      const newFiles = fileList.filter(
        (file) => !existingDocumentIdsRef.current.has(file.uid)
      );

      await Promise.all(
        newFiles.map(async (file) => {
          const uploadedFile = (file.response ?? file) as unknown as IFile;

          const publicInitiativeDocumentId = await uploadInitiativeDocument({
            initiativePublicId: savedinitiativePublicId,
            fileName: uploadedFile.name,
            filePath: uploadedFile.blobUrl,
            contentType: uploadedFile.type,
            blobListIds: uploadedFile.blobListIds,
          }).unwrap();

          existingDocumentIdsRef.current.add(publicInitiativeDocumentId);
          file.uid = publicInitiativeDocumentId;
        })
      );

      await Promise.all(
        Array.from(pendingDocumentRemovalsRef.current.entries()).map(
          async ([documentPublicId, filePath]) => {
            try {
              await softDeleteInitiativeDocument({
                initiativePublicId: savedinitiativePublicId,
                documentPublicId,
              }).unwrap();
            } catch (error) {
              console.error("Failed to soft delete initiative document", error);
              toast.error(intl.formatMessage({ id: "initiative.saveError" }));
              return;
            }

            const blobFolderPath = filePath.substring(
              0,
              filePath.lastIndexOf("/")
            );

            if (blobFolderPath) {
              try {
                await deleteUploads({
                  intakeId: blobFolderPath,
                  isInitiative: true,
                }).unwrap();
              } catch (error) {
                // Best-effort blob cleanup; don't block on failure since the document is already soft-deleted.
                console.error("Failed to delete blob uploads", error);
                toast.error(intl.formatMessage({ id: "initiative.saveError" }));
              }
            }
          }
        )
      );
      pendingDocumentRemovalsRef.current.clear();

     setFileList((previousFileList) => [...previousFileList]);
      toast.success(intl.formatMessage({ id: "initiative.saveSuccess" }));
      if (!isEditing) {
        navigateAfterSaveRef.current = `/initiatives/${savedinitiativePublicId}`;
      }
      setHasUnsavedChanges(false);
    } catch {
      toast.error(intl.formatMessage({ id: "initiative.saveError" }));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PageWrapper
      pageTitle={intl.formatMessage({
        id: isEditing ? "initiative.editPageTitle" : "initiative.createPageTitle",
      })}
      backButtonRoute={routes.initiatives.list}
    >
      <BlockingPopupModal
        isBlocking={hasUnsavedChanges}
        title={intl.formatMessage({ id: "initiative.unsavedChangesTitle" })}
        message={intl.formatMessage({ id: "initiative.unsavedChangesMessage" })}
        onOk={() => setHasUnsavedChanges(false)}
      />
      <Form
        form={form}
        layout="vertical"
        preserve
        scrollToFirstError
        onValuesChange={() => setHasUnsavedChanges(true)}
      >
        <StickyActionsHeader
          rightChildren={
            <SaveAndCancel
              pageName={PAGE_NAME}
              loading={isSubmitting}
              saveOnClick={handleSave}
              cancelOnClick={handleCancel}
            />
          }
        />
        <InitiativeFormFields
          projectTypeOptions={projectTypeOptions}
          productCategoryOptions={productCategoryOptions}
          fileList={fileList}
          initiativePublicId={initiativePublicId}
          placeholderId={placeholderId}
          beforeUpload={(file) => {
            setFileList((previousFileList) => [...previousFileList, file]);
            setHasUnsavedChanges(true);
          }}
          onRemove={handleRemoveDocument}
          onChange={handleDocumentsChange}
          onPreview={(file) => setPreviewDoc(file)}
        />
      </Form>
      {cdnFilePath && (
        <InitiativePreviewModal
          open={isModalOpen}
          cdnFilePath={cdnFilePath}
          previewFile={previewFile}
          onCancel={() => setIsModalOpen(false)}
          onDownload={downloadAndCloseModal}
        />
      )}
    </PageWrapper>
  );
};

export default Initiative;
